import React, {FC, useEffect, useState} from "react"

import {RouteComponentProps} from "react-router"
import {hangPromise} from "@irati/utils/promise"
import currencyService from "@irati/currency/services/currency"
import topupService from "@irati/wallet/services/TopUp"
import authentication from "@irati/auth"

import walletService from "@irati/wallet/graph/wallet"
import {WalletEntityType, Topup, PaymentCurrencyType, TiersResponse} from "@irati/types"
import {TopUpTypes} from "@irati/wallet/enums"
import {MutationCreateTopUpArgs} from "@irati/types/ops"
import {PartialRoute} from "@irati/utils/history/routing"
import Container from "../styled/Container"
import TopUp from "../../components/AdminReward/TopUpRewardPoints/TopUp"
import Checkout from "../../components/AdminReward/TopUpRewardPoints/Checkout"

export interface TopUpPayload {
  rpPoints: number
  purchaseCurrency: string
  rpExchangeRate: number
  type: string
  poNumber: string | null
}
export interface ServiceFeeValue {
  baseServiceFeeAmount: number
  tierServiceFeeAmount: number
}
export type TopUpOnChange = (payload: TopUpPayload) => void

const INITIAL_STATE = (): TopUpPayload => ({
  rpPoints: 0,
  purchaseCurrency: "", // The default purchase currency is based on the buService.getCurrencyCode()
  rpExchangeRate: 5,
  type: TopUpTypes.BankTransfer,
  poNumber: null
})

interface Props extends RouteComponentProps {}

const AdminRewardTopUp: FC<Props> = ({match}) => {
  const [rewardWalletTopup, setRewardWalletTopup] = useState<Topup>({})
  const [load, setLoad] = useState(hangPromise<any>())
  const [payload, setPayload] = useState(INITIAL_STATE())
  const [topupAmount, setTopupAmount] = useState(0)
  const [totalToPay, setTotalToPay] = useState(0)
  const [serviceFeeValue, setServiceFeeValue] = useState<ServiceFeeValue>({baseServiceFeeAmount: 0, tierServiceFeeAmount: 0})
  const [serviceFeesTiers, setServiceFeeTiers] = useState<TiersResponse[]>([])
  const tenantId = authentication.business().uuid

  useEffect(() => {
    const promise = topupService.getCurrencyCode().then(async (purchaseCurrency) => {
      const res = await currencyService.getFPCashbackForCurrency(purchaseCurrency)
      setPayload({...payload, purchaseCurrency, rpExchangeRate: res.rpExchangeRate})
    })
    const serviceFees = topupService.serviceFeesTiers().then(setServiceFeeTiers)
    setLoad([promise, serviceFees])
  }, [])
  useEffect(() => {
    const currentTier = serviceFeesTiers.find(({startValue, endValue}) =>
      endValue === -1 ? true : topupAmount >= startValue && topupAmount <= endValue
    )
    if (!currentTier) {
      return
    }
    const {baseServiceFeeRate, tierServiceFeeRate} = currentTier
    const serviceFeeValues = {
      baseServiceFeeAmount: Math.floor(topupAmount * baseServiceFeeRate!),
      tierServiceFeeAmount: Math.floor(topupAmount * tierServiceFeeRate!)
    }
    setServiceFeeValue(serviceFeeValues)
    setTotalToPay(topupAmount + serviceFeeValues.tierServiceFeeAmount)
  }, [serviceFeesTiers, topupAmount])
  const topUpSubmit = async () => {
    const wallet = await walletService.get(authentication.user().uuid!, PaymentCurrencyType.Rp, WalletEntityType.Reward)
    const payloadData: MutationCreateTopUpArgs = {
      amount: topupAmount,
      currency: payload.purchaseCurrency,
      tenantId,
      type: payload.type,
      walletUuid: wallet.uuid!,
      targetCurrency: PaymentCurrencyType.Rp,
      poNumber: payload.poNumber
    }
    const createdTopup = await topupService.create(payloadData)
    setRewardWalletTopup(createdTopup)
  }

  return (
    <Container>
      <PartialRoute
        match={match.path}
        path="/top-up"
        component={TopUp}
        componentProps={{
          payload,
          onPayloadChange: setPayload,
          load,
          topupAmount,
          totalToPay,
          onTopupAmountChange: setTopupAmount,
          serviceFeeValue,
          serviceFeesTiers
        }}
      />
      <PartialRoute
        match={match.path}
        path="/checkout"
        component={Checkout}
        componentProps={{
          payload,
          onPayloadChange: setPayload,
          createTopUp: topUpSubmit,
          rewardWalletTopup,
          topupAmount,
          totalToPay,
          serviceFeeValue
        }}
      />
    </Container>
  )
}

export default AdminRewardTopUp
